import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { calculateRemainingTime, getEffectiveDateFormat, getEffectiveTimeFormatBoolean } from '../utils/dateUtils';
import { calculateVisaValidity } from '../utils/timezoneUtils';
import TimeService from '../services/timeService';

export const useVisaTimer = (selectedDate, visaDuration, selectedCountry) => {
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [status, setStatus] = useState('');
  const [remainingTime, setRemainingTime] = useState({
    totalSeconds: 0,
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isValid: false
  });
  const [localTime, setLocalTime] = useState(null);
  const [localDate, setLocalDate] = useState(null);
  const [localCity, setLocalCity] = useState(null);
  const [localTimezone, setLocalTimezone] = useState(null);

  // Calculate validity info using country-specific timezone
  const validityInfo = useMemo(() => {
    if (!selectedDate || !visaDuration || !selectedCountry) return null;
    try {
      return calculateVisaValidity(selectedDate, Number(visaDuration), selectedCountry);
    } catch (error) {
      console.error('Error calculating visa validity:', error);
      return null;
    }
  }, [selectedDate, visaDuration, selectedCountry]);

  const expiryDate = validityInfo?.endDateTime || null;

  const updatingRef = useRef(false);
  const updateTimer = useCallback(() => {
    if (updatingRef.current || !validityInfo) return;
    updatingRef.current = true;
    try {
      // Use synchronized time instead of new Date()
      const timeService = TimeService;
      const now = timeService.getCurrentTime();
      
      const timeData = validityInfo.getRemainingTime(now);
      setRemainingTime(timeData);
      setStatus(timeData.totalSeconds <= 0 ? 'Invalid' : 'Valid');

      // Update local time and date
      if (validityInfo.countryTimezone) {
        // Use user's time format preference (or browser detection if 'auto')
        const is12Hour = getEffectiveTimeFormatBoolean();

        const timeFormatter = new Intl.DateTimeFormat('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: is12Hour,
          timeZone: validityInfo.countryTimezone
        });

        const dateFormatter = new Intl.DateTimeFormat('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric',
          timeZone: validityInfo.countryTimezone
        });

        setLocalTime(timeFormatter.format(now));
        setLocalDate(dateFormatter.format(now));

        // Handle city/capital
        const city = validityInfo.capital || (validityInfo.countryTimezone ? validityInfo.countryTimezone.split('/').pop().replace('_', ' ') : 'Local');
        setLocalCity(city);

        // Handle timezone - format as "EET, Egypt, Cairo" (abbreviation, country, capital)
        const countryName = typeof selectedCountry === 'string' ? selectedCountry : selectedCountry?.name;
        const timezoneParts = [];
        if (validityInfo.countryAbbr) {
          timezoneParts.push(validityInfo.countryAbbr);
        }
        if (countryName) {
          timezoneParts.push(countryName);
        }
        if (validityInfo.capital) {
          timezoneParts.push(validityInfo.capital);
        }
        const timezone = timezoneParts.length > 0 ? timezoneParts.join(', ') : 'Local';
        setLocalTimezone(timezone);
      }
    } finally {
      updatingRef.current = false;
    }
  }, [validityInfo]);

  useEffect(() => {
    if (!isConfirmed) return;
    updateTimer(); // Initial update
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [isConfirmed, updateTimer]);

  const confirmVisa = useCallback(() => {
    if (!selectedDate || visaDuration <= 0 || !selectedCountry) {
      return false;
    }
    updateTimer();
    setIsConfirmed(true);
    return true;
  }, [selectedDate, visaDuration, selectedCountry, updateTimer]);

  const resetVisa = useCallback(() => {
    setIsConfirmed(false);
    setStatus('');
    setRemainingTime({
      totalSeconds: 0,
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      isValid: false
    });
    setLocalTime(null);
    setLocalDate(null);
    setLocalCity(null);
    setLocalTimezone(null);
  }, []);

  return {
    isConfirmed,
    status,
    remainingTime,
    expiryDate,
    localTime,
    localDate,
    localCity,
    localTimezone,
    confirmVisa,
    resetVisa
  };
};

